/**
 * Wheel Collaboration — PURE invite-via-LINK helpers (P4b).
 *
 * Framework-free so the URL derivation + clipboard fallback are unit-testable
 * without React. The server returns a canonical ``join_url`` (built from its
 * WEB_URL), which we prefer; when it is absent we reconstruct the same shape
 * from the current origin + token so a copied link is never empty.
 */

/**
 * The shareable join URL for a link payload. Prefers the server's join_url;
 * otherwise builds ``<origin>/collaboration/join?token=<token>``.
 * @param {{join_url?: string, token?: string}|null|undefined} link
 * @param {string} [origin] defaults to window.location.origin
 * @returns {string} the URL, or "" when there is nothing to build from
 */
export function buildJoinUrl(link, origin) {
  if (!link) return "";
  if (link.join_url) return link.join_url;
  if (!link.token) return "";
  const base = (origin || (typeof window !== "undefined" ? window.location.origin : "")) || "";
  return `${base.replace(/\/$/, "")}/collaboration/join?token=${link.token}`;
}

/**
 * Copy text to the clipboard, resolving true on success. Uses the async
 * Clipboard API when available, else a hidden-textarea execCommand fallback so
 * copy still works on non-secure/older contexts. Never throws.
 * @param {string} text
 * @param {object} [deps] injectable navigator/document for tests
 * @returns {Promise<boolean>}
 */
export async function copyTextToClipboard(text, deps = {}) {
  const nav = deps.navigator || (typeof navigator !== "undefined" ? navigator : undefined);
  const doc = deps.document || (typeof document !== "undefined" ? document : undefined);
  if (!text) return false;
  try {
    if (nav && nav.clipboard && typeof nav.clipboard.writeText === "function") {
      await nav.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    // fall through to the execCommand path
  }
  try {
    if (!doc || typeof doc.execCommand !== "function") return false;
    const ta = doc.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    doc.body.appendChild(ta);
    ta.select();
    const ok = doc.execCommand("copy");
    doc.body.removeChild(ta);
    return !!ok;
  } catch (_e) {
    return false;
  }
}
